import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Message } from '@verclam/ui-kit';

const GENERIC_SSO_ERROR_MESSAGE = 'L’authentification auprès de votre fournisseur d’identité a échoué.';

/** Closed set of `sso_error` query values: an unknown key renders the generic wording, never itself. */
function resolveSsoErrorMessage(key: string): string {
	switch (key) {
		case 'provider_unreachable':
			return 'Le fournisseur d’identité de votre structure est injoignable. Réessayez plus tard ou contactez votre administrateur.';
		case 'request_expired':
			return 'Votre demande de connexion a expiré. Recommencez.';
		case 'unknown_account':
			return 'Aucun compte Dixit ne correspond à cette identité. Contactez votre administrateur.';
		case 'sso_not_allowed':
			return 'Votre compte se connecte avec un mot de passe. Utilisez le formulaire de connexion.';
		case 'identity_mismatch':
			return 'Cette identité ne correspond pas au compte Dixit associé à cette adresse. Contactez votre administrateur.';
		case 'email_not_verified':
			return 'Votre fournisseur d’identité ne confirme pas votre adresse e-mail. Contactez votre administrateur.';
		case 'account_disabled':
			return 'Votre compte a été désactivé. Contactez votre administrateur.';
		case 'account_locked':
			return 'Votre compte est temporairement verrouillé. Réessayez plus tard ou contactez votre administrateur.';
		case 'authentication_failed':
			return GENERIC_SSO_ERROR_MESSAGE;
		default:
			return GENERIC_SSO_ERROR_MESSAGE;
	}
}

/** Reads `?sso_error=<key>` once, strips the query string so a reload never repeats it, and renders
 * the matching French wording — `null` while there is none. */
const SsoErrorBanner: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();

	const [ssoErrorMessage, setSsoErrorMessage] = React.useState<string | null>(null);

	React.useEffect(() => {
		const errorKey = new URLSearchParams(location.search).get('sso_error');

		if (errorKey === null) {
			return;
		}

		setSsoErrorMessage(resolveSsoErrorMessage(errorKey));
		navigate(location.pathname, { replace: true });
	}, [location, navigate]);

	if (ssoErrorMessage === null) {
		return null;
	}

	return <Message severity="error" text={ssoErrorMessage} />;
};

export default SsoErrorBanner;
